import type { FormEvent } from 'react';
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { API_BASE_URL } from '@/utils/env';
import { BRAND, brandLabel } from '@/brand';
import { auth } from '../firebase';
import { signInWithCustomToken } from 'firebase/auth';
import '../styles/authPages.css';

const LOGO_URL = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png';

const CONS_VARS: React.CSSProperties = {
  '--cream': '#FCFDFF',
  '--accent': '#1976d2',
  '--accent-deep': '#3171af',
  '--on-accent': '#fff',
  '--accent-glow': 'rgba(91,155,213,.30)',
  '--accent-tint': '#e1f5fe',
  '--accent-ink': '#1465b3',
  '--line': '#E5E8EA',
  '--line-strong': '#CFD6DB',
  '--card-line': '#EEF0F2',
  '--field-bg': '#FCFDFF',
  '--ring': 'rgba(25,118,210,.18)',
  '--link': '#1565b3',
} as React.CSSProperties;

function ConsultingLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (!email.trim() || !password.trim()) { setError('Please enter both email and password'); return; }
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        let errorMessage = 'Login failed. Please try again.';
        try {
          const data = await response.json();
          if (data.error) errorMessage = data.error;
          else if (response.status === 401) errorMessage = 'Invalid email or password. Please check your credentials.';
          else if (response.status === 403) errorMessage = 'Please verify your email before logging in. Check your inbox for the verification link.';
          else if (response.status === 400) errorMessage = 'Email and password are required.';
          else if (response.status === 500) errorMessage = 'Server error. Please try again later.';
        } catch {}
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Login failed. Please try again.');

      // Brand gate: an account created on the other domain can't sign in here.
      const acctBrand = data.profile?.brand || data.user?.brand;
      if (acctBrand && acctBrand !== BRAND) {
        setLoading(false);
        setError(`This account is registered on ${brandLabel(acctBrand)}. Please sign in there.`);
        return;
      }

      if (data.user) {
        const idToken = data.tokens?.idToken;
        localStorage.setItem('user', JSON.stringify({ uid: data.user.uid, email: data.user.email, displayName: data.user.displayName, brand: acctBrand || BRAND }));
        if (data.tokens) {
          const storage = rememberMe ? localStorage : sessionStorage;
          storage.setItem('authTokens', JSON.stringify(data.tokens));
          storage.setItem('token', data.tokens.idToken);
        }
        localStorage.setItem('activeServiceView', 'consulting');

        // Sign in to Firebase client SDK so Firestore/RTDB client operations work
        if (data.customToken) {
          await signInWithCustomToken(auth, data.customToken).catch(e => console.warn('Firebase client sign-in failed:', e));
        }

        try {
          const userDataRes = await fetch(`${API_BASE_URL}/auth/user/${data.user.uid}`, {
            headers: { Authorization: `Bearer ${idToken}` }
          });
          if (userDataRes.ok) {
            const userDataJson = await userDataRes.json();
            const userData = userDataJson.user;
            if (!userData?.country) { navigate('/Countrysl', { replace: true }); return; }
            if (!userData?.goalAssessment?.group) { navigate('/readiness-journey', { replace: true }); return; }
            navigate('/dashboard', { replace: true });
            return;
          } else {
            navigate('/Countrysl', { replace: true });
            return;
          }
        } catch {
          navigate(from, { replace: true });
          return;
        }
      }
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message === 'Failed to fetch' || err.name === 'TypeError'
        ? 'Cannot connect to server. Please check your internet connection or try again later.'
        : err.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) { setError('Failed to get Google credentials'); return; }
    setLoading(true); setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/google-signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as any).error || 'Failed to sign in with Google');
      }

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to sign in with Google');

      // Brand gate: an account created on the other domain can't sign in here.
      const gBrand = data.profile?.brand || data.user?.brand;
      if (gBrand && gBrand !== BRAND) {
        setLoading(false);
        setError(`This account is registered on ${brandLabel(gBrand)}. Please sign in there.`);
        return;
      }

      if (data.user) {
        localStorage.setItem('user', JSON.stringify({ uid: data.user.uid, email: data.user.email, displayName: data.user.displayName, firstName: data.user.firstName, lastName: data.user.lastName, photoURL: data.user.photoURL, brand: gBrand || BRAND }));
        if (data.tokens) {
          localStorage.setItem('authTokens', JSON.stringify(data.tokens));
          localStorage.setItem('token', data.tokens.idToken);
          localStorage.setItem('idToken', data.tokens.idToken);
          if (data.tokens.refreshToken) localStorage.setItem('refreshToken', data.tokens.refreshToken);
        }
        localStorage.setItem('activeServiceView', 'consulting');

        // Sign in to Firebase client SDK
        if (data.customToken) {
          await signInWithCustomToken(auth, data.customToken).catch(e => console.warn('Firebase client sign-in failed:', e));
        }

        // Check admin
        try {
          const adminsRes = await fetch(`${API_BASE_URL}/auth/admins`, { headers: { Authorization: `Bearer ${data.tokens.idToken}` } });
          const adminsData = await adminsRes.json();
          const me = Array.isArray(adminsData.admins)
            ? adminsData.admins.find((a: any) => a.uid === data.user.uid || a.email === data.user.email)
            : null;
          const isAdmin = adminsData.success && !!me;
          if (isAdmin) {
            // uid is recorded so the panel can build this admin's client-invite
            // link without another round trip. See utils/adminIdentity.
            localStorage.setItem('adminUser', JSON.stringify({ email: data.user.email, uid: me?.uid || data.user.uid || null, role: me?.role || 'admin', loginTime: new Date().toISOString() }));
            localStorage.setItem('isAdmin', 'true');
            navigate('/admin', { replace: true });
            return;
          }
        } catch {}

        try {
          const userDataRes = await fetch(`${API_BASE_URL}/auth/user/${data.user.uid}`, { headers: { Authorization: `Bearer ${data.tokens.idToken}` } });
          if (userDataRes.ok) {
            const userDataJson = await userDataRes.json();
            const userData = userDataJson.user;
            if (!userData?.country) { navigate('/Countrysl', { replace: true }); return; }
            if (!userData?.goalAssessment?.group) { navigate('/readiness-journey', { replace: true }); return; }
            navigate('/dashboard', { replace: true });
            return;
          }
          navigate('/Countrysl', { replace: true });
        } catch {
          navigate('/Countrysl', { replace: true });
        }
      }
    } catch (err: any) {
      setError(err.message === 'Failed to fetch' || err.name === 'TypeError'
        ? 'Cannot connect to server. Please check your internet connection.'
        : err.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell" style={{ ...CONS_VARS, minHeight: '100vh' } as React.CSSProperties}>
      <div className="shell">
        {/* LEFT RAIL */}
        <aside className="rail">
          <div className="rl-logo">
            <a href="/" style={{ lineHeight: 0 }}>
              <img src={LOGO_URL} alt="Conncct" style={{ height: 34, width: 'auto', filter: 'brightness(0) invert(1)' }} />
            </a>
          </div>
          <div className="rl-body">
            <span className="rl-eyebrow">Capital readiness</span>
            <h2>Get investor-ready. Then <span className="hl">raise with confidence.</span></h2>
            <p className="rl-lead">Your assessments, financial model and advisor sessions, tracked in one place.</p>
            <div className="mock">
              <div className="mock-top"><b>Readiness score</b><span className="chip">Live</span></div>
              <div className="donut">
                <svg viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="#E7ECF4" strokeWidth="11"/>
                  <circle cx="60" cy="60" r="52" fill="none" stroke="url(#cg-l)" strokeWidth="11" strokeLinecap="round" strokeDasharray="222 327" transform="rotate(-90 60 60)"/>
                  <defs><linearGradient id="cg-l" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#1976d2"/><stop offset="1" stopColor="#5B9BD5"/></linearGradient></defs>
                </svg>
                <div className="val tnum">68%</div>
              </div>
              <div className="donut-cap">Target 80%, the investor-ready line</div>
              <div className="ck">
                <span className="d"><svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" stroke="#15824E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                <span className="x"><b>Financial model</b><small>Investor-grade</small></span>
                <span className="st done">Done</span>
              </div>
              <div className="ck">
                <span className="d"><svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" stroke="#15824E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                <span className="x"><b>Pitch deck</b><small>Narrative aligned</small></span>
                <span className="st done">Done</span>
              </div>
              <div className="ck">
                <span className="d due"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" stroke="#9A6B00" strokeWidth="2.2" fill="none"/><path d="M12 7v5l3 2" stroke="#9A6B00" strokeWidth="2.3" strokeLinecap="round" fill="none"/></svg></span>
                <span className="x"><b>Data room</b><small>2 documents outstanding</small></span>
                <span className="st due">In progress</span>
              </div>
            </div>
          </div>
          <div className="rl-foot">
            <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Bank-grade encryption · your data is never sold
          </div>
        </aside>

        {/* RIGHT PANE */}
        <main className="pane">
          <div className="topbar">
            <a className="top-pill" href="/consulting/signup">Create account</a>
          </div>
          <div className="center">
            <section className="card">
              <span className="eyebrow">Welcome back</span>
              <h1>Welcome <span className="hl">back</span>.</h1>
              <p className="sub">Sign in to pick up your readiness score and consulting threads.</p>

              {error && <div className="err-msg">{error}</div>}

              <form onSubmit={handleSubmit} noValidate>
                <div className="field">
                  <label htmlFor="email-cl">Email <span style={{ color: '#E0584B', marginLeft: 2 }}>*</span></label>
                  <input className="input" id="email-cl" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" disabled={loading} />
                </div>

                <div className="field has-icon">
                  <label htmlFor="pw-cl">Password <span style={{ color: '#E0584B', marginLeft: 2 }}>*</span></label>
                  <div className="control">
                    <input className="input" id="pw-cl" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" disabled={loading} />
                    <button type="button" className="toggle" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      <svg viewBox="0 0 24 24">
                        {showPassword
                          ? <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/><line x1="3" y1="3" x2="21" y2="21"/></>
                          : <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></>
                        }
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="row">
                  <label className="check">
                    <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} disabled={loading} />
                    <span className="box">
                      <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                    </span>
                    Remember me
                  </label>
                  <Link to="/forgot-password" className="link">Forgot password?</Link>
                </div>

                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>

              <div className="divider">or continue with</div>
              <div className="google-wrap">
                <GoogleLogin
                  onSuccess={handleGoogleSignIn}
                  onError={() => setError('Google Sign-In failed. Please try again or use email/password.')}
                  text="signin_with"
                  size="large"
                  width="400"
                  theme="outline"
                  shape="rectangular"
                />
              </div>

              <p className="switch">Don't have an account? <a href="/consulting/signup">Create one</a></p>
              <p className="legal">By continuing you agree to our <a href="/privacy">Terms</a> &amp; <a href="/privacy">Privacy Policy</a>.</p>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default ConsultingLogin;
